import {
  AutocompleteInput,
  AutocompleteArrayInput,
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  DatagridConfigurable,
  Edit,
  EditButton,
  FilterButton,
  FunctionField,
  List,
  ReferenceInput,
  ReferenceArrayField,
  ReferenceArrayInput,
  required,
  SearchInput,
  SelectColumnsButton,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  ChipField,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
  WrapperField,
} from "react-admin";

import { Alert, Chip, Typography } from "@mui/material";
import LocalisedDateField from "../components/localisedDateField";

export interface Locale {
  id: string;
  active: boolean;
  name: string;
  country: string;
  countryEmoji: string;
  currency: string;
  language: string;
  supportedCurrencies: string[];
  supportedLanguages: string[];
  featureFlagIds: string[];
}

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <SelectColumnsButton />
    <FilterButton />
  </TopToolbar>
);

const userFilters = [<SearchInput source="name__icontains" alwaysOn={true} placeholder="Name" />];

export const LocalesList = (props: any) => {
  return (
    <List
      {...props}
      actions={<ListActions />}
      filters={userFilters}
      title={"Locales"}
      sort={{ field: "updated_at", order: "DESC" }}
    >
      <DatagridConfigurable bulkActionButtons={false}>
        <TextField source="name" />
        <FunctionField
          render={(record: any) => (
            <span title={record.country} style={{ fontSize: "24px" }}>
              {record.countryEmoji}
            </span>
          )}
          label="Country"
        />
        <TextField source="currency" label="Currency" />
        <BooleanField label="Supported in Webshop" source="supportsWebshop" />
        <FunctionField
          render={(record: any) => (
            <LocalisedDateField record={record} field="createdAt" compact={true} showTime={true} />
          )}
          label="Created At"
        />
        <FunctionField
          render={(record: any) => (
            <LocalisedDateField record={record} field="updatedAt" compact={true} showTime={true} />
          )}
          label="Updated At"
        />
        <WrapperField label="Actions">
          <ShowButton />
          <EditButton />
        </WrapperField>
      </DatagridConfigurable>
    </List>
  );
};

const LanguageAndCountryFilterToQuery = (query: string) => {
  if (query.length == 2) {
    return { id__exact: query };
  }
  return { name__icontains: query };
};

export const LocaleCreate = () => {
  return (
    <Create redirect="show">
      <SimpleForm>
        <TextInput source="name" validate={required()} />
        <ReferenceInput source="country" reference="allCountries">
          <AutocompleteInput
            optionText={(option) => {
              return `${option.flag} ${option.id} - ${option.name}`;
            }}
            filterToQuery={LanguageAndCountryFilterToQuery}
            sx={{ width: "300px" }}
            validate={required()}
          />
        </ReferenceInput>

        <ReferenceInput source="currency" reference="allCurrencies">
          <AutocompleteInput
            sx={{ minWidth: "300px" }}
            optionText={(record: any) => `${record.id} - ${record.name}`}
            validate={required()}
          />
        </ReferenceInput>
        <ReferenceInput source="language" reference="allLanguages">
          <AutocompleteInput
            sx={{ minWidth: "300px" }}
            filterToQuery={LanguageAndCountryFilterToQuery}
            optionText={(record: any) => `${record.id} - ${record.name}`}
            validate={required()}
          />
        </ReferenceInput>
      </SimpleForm>
    </Create>
  );
};

const LocaleShowAside = () => {
  const record = useRecordContext();
  return (
    <div style={{ width: 200, margin: "1em" }}>
      <Typography variant="h6">Metadata</Typography>
      <p>
        Status:{" "}
        <Chip label={record.active ? "Active" : "Archived"} color={record.active ? "success" : "error"} size="small" />
      </p>
      <p>
        Created <LocalisedDateField record={record} field="createdAt" showTime={true} />
      </p>
      <p>
        Updated <LocalisedDateField record={record} field="updatedAt" showTime={true} />{" "}
      </p>
      <p>
        Created by: <TextField record={record} source="createdBy" />
      </p>
      <p>
        Modified by: <TextField record={record} source="modifiedBy" />
      </p>
      <p>
        <FunctionField label="Modified By" render={(record: any) => record?.modifiedBy || ""} />
      </p>
    </div>
  );
};

const LocaleShowTitle = () => {
  const locale: Locale = useRecordContext();
  if (!locale) return null;
  return (
    <span>
      Locale - {locale.name} {locale.countryEmoji}
    </span>
  );
};

export const LocaleShow = () => {
  return (
    <Show aside={<LocaleShowAside />} title={<LocaleShowTitle />}>
      <SimpleShowLayout>
        <TextField source="id" />
        <TextField source="name" />
        <FunctionField
          label="Country"
          render={(locale: Locale) => {
            return (
              <>
                {locale.country} - {locale.country}{" "}
                <span style={{ fontSize: "24px", verticalAlign: "middle" }}>{locale.countryEmoji}</span>
              </>
            );
          }}
        />
        <TextField source="currency" label="Currency" />
        <FunctionField
          label="Language"
          render={(locale: Locale) => {
            return (
              <>
                {locale.language} - {locale.country}
              </>
            );
          }}
        />
        <ReferenceArrayField label="Feature Flags" reference="featureFlags" source="featureFlagIds">
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
      </SimpleShowLayout>
    </Show>
  );
};

export const LocaleEdit = () => {
  return (
    <Edit>
      <SimpleForm>
        <Alert severity="info" sx={{ mb: 2 }}>
          Feature flags selected here apply to every user in <b>this locale&apos;s country</b>, not only this language
          locale.
        </Alert>
        <TextInput source="name" validate={required()} />
        <ReferenceInput source="country" reference="allCountries">
          <AutocompleteInput
            optionText={(option) => {
              return `${option.flag} ${option.id} - ${option.name}`;
            }}
            filterToQuery={LanguageAndCountryFilterToQuery}
            sx={{ width: "300px" }}
            validate={required()}
          />
        </ReferenceInput>
        <BooleanInput label="Supported in Webshop" source="supportsWebshop" />

        <ReferenceInput source="language" reference="allLanguages">
          <AutocompleteInput
            sx={{ minWidth: "300px" }}
            filterToQuery={LanguageAndCountryFilterToQuery}
            optionText={(record: any) => `${record.id} - ${record.name}`}
            validate={required()}
          />
        </ReferenceInput>
        <ReferenceArrayInput source="featureFlagIds" reference="featureFlags" perPage={250}>
          <AutocompleteArrayInput
            label="Feature Flags"
            getOptionDisabled={(record: any) => record.enabled && !record.emails?.length && !record.localeIds?.length}
            optionText={(record: any) =>
              `${record.name}${
                record.enabled && !record.emails?.length && !record.localeIds?.length ? " (globally available)" : ""
              }`
            }
            filterToQuery={(query: string) => ({ name__icontains: query })}
            helperText="Globally available flags must have their audience changed from the feature flag page."
            fullWidth
          />
        </ReferenceArrayInput>
      </SimpleForm>
    </Edit>
  );
};
